import React, { useState, useEffect, JSX } from "react";
import { UserAvatar } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { UserOnEntity } from "@/graphql/__generated__/graphql";
import { UseFormReturn } from "react-hook-form";
import { selectUserFromMention } from "./utils";
import { cn } from "@/lib/utils";

interface Props {
  filteredUsers: UserOnEntity[];
  inputRef: React.RefObject<HTMLInputElement | HTMLTextAreaElement>;
  form: UseFormReturn<any>;
  formField: string;
  setMentionQuery: (_mentionQuery: string | null) => void;
};

/**
 * ChatMentionsPopup component renders a popup list of user mention suggestions.
 *
 * When a user is selected from the list via a mouse down event, this component triggers
 * the replacement of the partial mention in the input field with the user's full name.
 *
 * @param {Object} props - The component props.
 * @param {UserOnEntity[]} props.filteredUsers - An array of filtered user objects to display.
 * @param {React.RefObject<HTMLInputElement | HTMLTextAreaElement>} props.inputRef - The reference to the input element.
 * @param {UseFormReturn<any>} props.form - The react-hook-form instance.
 * @param {string} props.formField - The name of the form field corresponding to the mention input.
 * @param {function(string|null): void} props.setMentionQuery - Function to update the mention query state.
 * @returns {JSX.Element} The rendered ChatMentionsPopup component.
 */
const ChatMentionsPopup = ({ filteredUsers, inputRef, form, formField, setMentionQuery }: Props): JSX.Element => {
  // New state to track the currently highlighted mention suggestion
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  // Reset the highlight whenever the filtered users change.
  useEffect(() => {
    setHighlightedIndex(0);
  }, [filteredUsers]);

  // Add a keydown event listener for arrow and enter keys:
  // useEffect hook to add keyboard navigation for mention suggestions.
  useEffect(() => {
    // If there are no filtered users, do not setup the keydown event listener.
    if (filteredUsers.length === 0) return;

    // Define the keydown event handler to handle arrow and enter key interactions.
    const handleKeyDown = (e: KeyboardEvent) => {
      // When the "ArrowDown" key is pressed, move the highlight to the next suggestion.
      if (e.key === "ArrowDown") {
        // Prevent default scrolling and stop the event from propagating.
        e.preventDefault();
        e.stopPropagation();
        // Increment the highlighted index and wrap around using modulo if needed.
        setHighlightedIndex(prev => (prev + 1) % filteredUsers.length);
      }
      // When the "ArrowUp" key is pressed, move the highlight to the previous suggestion.
      else if (e.key === "ArrowUp") {
        // Prevent default behavior and stop propagation.
        e.preventDefault();
        e.stopPropagation();
        // Decrement the current index and wrap around to the end if necessary.
        setHighlightedIndex(prev => (prev - 1 + filteredUsers.length) % filteredUsers.length);
      }
      // When the "Enter" key is pressed, accept the highlighted suggestion.
      else if (e.key === "Enter") {
        // Prevent any default actions such as form submission.
        e.preventDefault();
        e.stopPropagation();
        // If a valid suggestion is highlighted, call the selectUser function to replace the mention.
        if (filteredUsers[highlightedIndex]) {
          selectUserFromMention(filteredUsers[highlightedIndex], inputRef, form, formField, setMentionQuery);
        }
      }
    };

    // Attach the keydown event listener using the capturing phase so it fires before parent listeners.
    document.addEventListener("keydown", handleKeyDown, true);
    // Cleanup: remove the event listener using the same capturing flag.
    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [filteredUsers, highlightedIndex, inputRef, form, formField, setMentionQuery]);

  // Auto-scroll the highlighted suggestion into view
  useEffect(() => {
    const el = document.getElementById(`chat-mention-${highlightedIndex}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [highlightedIndex]);

  return (
    <Card className="absolute left-0 right-0 bottom-full mb-2 z-10 max-w-[300px]">
      <div className="max-h-[200px] overflow-y-auto">
        {filteredUsers.map((user, index) => (
          <div
            id={`chat-mention-${index}`}
            key={user.id}
            className={cn(
              "flex items-center gap-1 px-4 py-2 cursor-pointer text-sm",
              index === highlightedIndex ? "bg-slate-200" : "hover:bg-slate-100"
            )}
            onMouseDown={(e) => {
              e.preventDefault();
              selectUserFromMention(user, inputRef, form, formField, setMentionQuery);
            }}
            onMouseEnter={() => setHighlightedIndex(index)}
          >
            <UserAvatar user={user as any} />
            <p>
              {user.firstname} {user.lastname}
            </p>
          </div>
        ))}
      </div>
    </Card>
  );
};

export default ChatMentionsPopup;
